import {
  Component,
  DestroyRef,
  OnDestroy,
  OnInit,
  computed,
  effect,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { ButtonComponent } from '@purolator/ui-kit-angular';
import { LocatorApiService } from '../../core/api/locator/locator-api.service';
import { PickupApiService } from '../../core/api/pickup/pickup-api.service';
import type {
  PickupAvailabilitySlot,
  PickupValidateRequest,
  PickupValidateResponse,
} from '../../core/api/pickup/models/pickup.model';
import { AnalyticsService } from '../../core/integrations/analytics';
import { TranslatePipe, TranslationService } from '../../core/i18n';
import {
  SHIPMENT_WIZARD_STEPS,
  ShipmentFlowStateService,
} from '../../core/services/shipment-flow-state.service';
import { SkeletonBlock } from '../../shared/components/skeleton/skeleton-block';
import type { AddressFormValue } from '../ship-from-ship-to/models/address-form.model';
import { redirectIfCompletedShipmentActive } from '../get-labels/utils/redirect-if-completed-shipment-active';
import { ShipmentStepper } from '../ship-from-ship-to/sections/shipment-stepper/shipment-stepper';
import {
  buildAdditionalCoverageSavePayload,
  createAdditionalCoverageForm,
  isAdditionalCoverageValid,
} from './forms/additional-coverage.form';
import {
  buildSignatureProtectionSavePayload,
  createSignatureProtectionForm,
  isSignatureProtectionValid,
  type SignatureRequirement,
} from './forms/signature-protection.form';
import {
  inclusiveSignatureRequirements,
  resolveDestinationResidential,
  resolveSignatureRequirement,
} from './forms/resolve-signature-options';
import { AdditionalCoverageSection } from './sections/additional-coverage-section/additional-coverage-section';
import { DeliveryServiceSelection } from './sections/delivery-service-selection/delivery-service-selection';
import type { EstimateBreakdownView } from './sections/delivery-service-selection/delivery-service-matrix';
import type { SelectedDeliveryService } from './sections/delivery-service-selection/delivery-service-selection.model';
import { EstimatedCostsPanel } from './sections/delivery-service-selection/estimated-costs-panel';
import {
  buildWindowOptionsForSlot,
  formatCurrentLocalHhmm,
  formatCurrentLocalTime,
  formatLocalIsoDate,
  hhmmToMinutes,
  mapValidatePickUpsToDateSelection,
  resolveMinimumPickupWindowMinutes,
  resolvePickupWindowSelection,
  selectPickupDateDefaults,
  type ShipFromLocalClockOptions,
} from './sections/pickup-drop-off/mappers/map-pickup-availability';
import {
  mapPickupAvailabilityNotice,
  pickupAvailabilityNoticeI18nKey,
  type PickupAvailabilityNoticeKind,
} from './sections/pickup-drop-off/mappers/map-pickup-availability-notice';
import { prepareSchedulePickupRequest } from './sections/pickup-drop-off/mappers/prepare-schedule-pickup-request';
import { resolveDefaultPickupLocationCode } from './sections/pickup-drop-off/mappers/build-pickup-location-options';
import {
  resolvePickupRouteCodes,
  type PickupRouteCodes,
} from './sections/pickup-drop-off/mappers/resolve-pickup-route-codes';
import { PickupDropOffComponent } from './sections/pickup-drop-off/pickup-drop-off.component';
import type { InductionMethod } from './sections/pickup-drop-off/pickup-drop-off.state';
import {
  buildPickupDropOffSavePayload,
  createInitialPickupDropOffState,
  hydratePickupDropOffFromSavePayload,
  isPickupDropOffValid,
  setInductionMethod,
} from './sections/pickup-drop-off/pickup-drop-off.state';
import { SignatureProtectionSection } from './sections/signature-protection/signature-protection-section';
import type { PickupWindowChange } from './sections/pickup-drop-off/pickup-window/pickup-window-section.component';
import type { PickupLocationsLanguage } from '../../core/api/pickup/pickup-api.service';

/** Customer-facing pickup window notice kinds under `pickupDropOff.windowErrors`. */
type PickupWindowNoticeKind =
  'windowTooSmall' | 'unableToScheduleSelectedTime' | 'noValidWindowForDate';

/**
 * Router state key used when redirecting to Ship From / Ship To after a missing
 * Service Ability attestation on a later wizard step (OM-770).
 */
export const SERVICE_ABILITY_MISSING_SYSTEM_ERROR_STATE_KEY = 'showSystemError';

/** Approved generic system error shown on the address step after an SA-token redirect. */
export const SERVICE_ABILITY_MISSING_SYSTEM_ERROR_KEY =
  'createShipment.system.unexpectedError' as const;

/** In-memory cache for `GET /pickup/locations` within a Service-step visit. */
interface PickupLocationsCache {
  language: PickupLocationsLanguage;
  pickupFrom: Record<string, string>;
}

/**
 * Create-shipment Service step (step 3).
 * Composes delivery date/service selection, signature protection, Additional Coverage
 * (declared value), and Pickup/Drop-off induction; loads nearest drop-off via locator when
 * Drop-off is selected, and pickup validate/locations when Schedule a pickup is selected.
 */
@Component({
  selector: 'app-service-step',
  imports: [
    ShipmentStepper,
    DeliveryServiceSelection,
    EstimatedCostsPanel,
    AdditionalCoverageSection,
    SignatureProtectionSection,
    PickupDropOffComponent,
    ButtonComponent,
    SkeletonBlock,
    TranslatePipe,
  ],
  templateUrl: './service-step.html',
  styleUrl: './service-step.scss',
})
export class ServiceStep implements OnInit, OnDestroy {
  private readonly shipmentFlowState = inject(ShipmentFlowStateService);
  private readonly router = inject(Router);
  private readonly pickupApi = inject(PickupApiService);
  private readonly locatorApi = inject(LocatorApiService);
  private readonly analytics = inject(AnalyticsService);
  private readonly translation = inject(TranslationService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly deliverySelection = viewChild(DeliveryServiceSelection);
  /** True after a Review-return hydrate so the first product apply keeps the persisted signature. */
  private signatureSelectionCommitted = false;
  /** Last courier product whose signature options were applied; same-product re-rates keep the current value. */
  private lastSignatureProductCode: string | null = null;
  protected readonly signatureAvailableRequirements = signal<SignatureRequirement[] | null>(null);
  protected readonly signatureIncludedRequirements = signal<SignatureRequirement[]>([]);
  /** Destination residential for complimentary “(Included)” labeling on Signature Required. */
  protected readonly signatureDestinationResidential = signal(true);

  protected readonly activeStepIndex = 2;
  protected readonly pickupSchedulingEnabled = signal(true);
  protected readonly pickupDropOffState = signal(createInitialPickupDropOffState());
  protected readonly additionalCoverageForm = createAdditionalCoverageForm();
  protected readonly signatureProtectionForm = createSignatureProtectionForm();
  /**
   * True while the delayed delivery/costs skeleton is visible (same rate-call region).
   */
  protected readonly showDeliverySkeleton = signal(false);
  /**
   * True until delivery offerings settle (ready or error). Starts true so signature/coverage
   * do not flash during the pre-skeleton window before a skeleton may paint.
   */
  protected readonly deliveryOfferingsUnresolved = signal(true);

  /**
   * True while secondary delivery-dependent sections and Save should stay gated.
   */
  protected readonly deliveryRegionBusy = computed(
    () => this.deliveryOfferingsUnresolved() || this.showDeliverySkeleton(),
  );
  protected readonly showValidationErrors = signal(false);
  /** True while Save and Continue async work (pickup window validate + navigate) is in flight. */
  protected readonly saveAndContinueInProgress = signal(false);
  protected readonly hasDeliverySelection = signal(false);
  protected readonly estimatedBreakdown = signal<EstimateBreakdownView | null>(null);
  protected readonly shipFrom = signal<AddressFormValue | null>(null);
  protected readonly availabilityLoading = signal(false);
  /**
   * Last mapped pickup availability notice kind (e.g. notSupportedAtAddress).
   * Drives scenario-specific UI such as the Drop-off link alert.
   */
  protected readonly availabilityNoticeKind = signal<PickupAvailabilityNoticeKind | null>(null);
  /**
   * Whether the availability kind should render as an error alert, informational notice, or neither
   * (rich not-supported-at-address UI). Kept separate from the kind so copy can retranslate on locale change.
   */
  private readonly availabilityMessageSlot = signal<'error' | 'notice' | null>(null);
  /** Locale-reactive availability error text derived from kind + slot + translation revision. */
  protected readonly availabilityError = computed(() => {
    this.translation.revision();
    const kind = this.availabilityNoticeKind();
    if (this.availabilityMessageSlot() !== 'error' || !kind) {
      return null;
    }
    return this.translateAvailabilityNotice(kind);
  });
  /** Locale-reactive availability notice text derived from kind + slot + translation revision. */
  protected readonly availabilityNotice = computed(() => {
    this.translation.revision();
    const kind = this.availabilityNoticeKind();
    if (this.availabilityMessageSlot() !== 'notice' || !kind) {
      return null;
    }
    return this.translateAvailabilityNotice(kind);
  });
  /** Locale-independent pickup window notice kind; display text is derived in `windowNotice`. */
  private readonly windowNoticeKind = signal<PickupWindowNoticeKind | null>(null);
  /** Locale-reactive pickup window notice text. */
  protected readonly windowNotice = computed(() => {
    this.translation.revision();
    const kind = this.windowNoticeKind();
    return kind ? this.translateWindowNotice(kind) : null;
  });
  protected readonly locationsLoading = signal(false);
  /** True when pickup locations failed to load; message is derived in `locationsError`. */
  private readonly locationsErrorActive = signal(false);
  /** Locale-reactive pickup locations load error. */
  protected readonly locationsError = computed(() => {
    this.translation.revision();
    return this.locationsErrorActive()
      ? this.translation.translate('createShipment.serviceStep.pickupDropOff.locationsError')
      : null;
  });
  protected readonly locatorLoading = signal(false);
  /** True when nearest drop-off lookup failed or returned empty; message is derived in `locatorError`. */
  private readonly locatorErrorActive = signal(false);
  /** Locale-reactive nearest drop-off locator error. */
  protected readonly locatorError = computed(() => {
    this.translation.revision();
    return this.locatorErrorActive()
      ? this.translation.translate(
          'createShipment.serviceStep.pickupDropOff.nearestLocation.unavailable',
        )
      : null;
  });

  /**
   * True when validate mapped to notSupportedAtAddress (PICKUP_ERROR_01).
   */
  protected readonly pickupNotSupportedAtAddress = computed(
    () => this.availabilityNoticeKind() === 'notSupportedAtAddress',
  );

  private pickupLoadGeneration = 0;
  private locatorLoadGeneration = 0;
  /** Cached locations response for this Service-step visit; refetch only when empty or locale changes. */
  private pickupLocationsCache: PickupLocationsCache | null = null;
  /** Deduplicates concurrent locations fetches per Accept-Language (prefetch vs loadPickupData). */
  private pickupLocationsInFlight: Partial<
    Record<PickupLocationsLanguage, Promise<Record<string, string> | null>>
  > = {};
  /** Enter-time `POST /pickup/validate` body reused until ship-from or route codes change. */
  private pickupValidateCache: { key: string; response: PickupValidateResponse } | null = null;
  /** Last pickup/locator route key applied from a delivery selection change. */
  private lastPickupRouteKey: string | null = null;

  /**
   * Refetches pickup-from labels when the UI locale changes so Schedule a pickup
   * shows EN/FR API strings that match the active language.
   * Cold-start fetch stays in bootstrapPickupOnEnter after pickupEnabled is known.
   */
  private readonly syncPickupLocationsOnLocaleChange = effect(() => {
    const language: PickupLocationsLanguage = this.translation.locale() === 'fr' ? 'FR' : 'EN';
    if (!this.pickupSchedulingEnabled()) {
      return;
    }
    const cache = this.pickupLocationsCache;
    if (!cache || cache.language === language) {
      return;
    }
    void this.prefetchPickupLocations();
  });

  /**
   * Redirects when the Package step is incomplete or Service Ability attestation is missing.
   * Hydrates delivery selection, coverage, and pickup/drop-off from flow state.
   * Restores signature only when the Service step is still complete (Review return);
   * otherwise OM-347 defaults apply after address/package invalidation (OM-725).
   */
  ngOnInit(): void {
    if (redirectIfCompletedShipmentActive(this.shipmentFlowState, this.router)) {
      return;
    }

    const state = this.shipmentFlowState.load();

    if (!state?.completedSteps.includes(SHIPMENT_WIZARD_STEPS.package)) {
      this.router.navigateByUrl('/package').catch(() => undefined);
      return;
    }

    if (!state.attestations?.serviceAbility?.trim()) {
      void this.router.navigateByUrl('/', {
        state: {
          [SERVICE_ABILITY_MISSING_SYSTEM_ERROR_STATE_KEY]:
            SERVICE_ABILITY_MISSING_SYSTEM_ERROR_KEY,
        },
      });
      return;
    }

    this.shipFrom.set(state.addresses?.shipFrom ?? null);

    if (state.additionalCoverage) {
      const { declaredValue, packageDescription } = state.additionalCoverage;
      this.additionalCoverageForm.patchValue({
        declaredValue: declaredValue != null ? String(declaredValue) : '',
        packageDescription: packageDescription ?? '',
      });
    }

    if (state.signatureProtection && state.completedSteps.includes(SHIPMENT_WIZARD_STEPS.service)) {
      this.signatureProtectionForm.patchValue({
        requirement: state.signatureProtection.requirement,
      });
      this.signatureSelectionCommitted = true;
    }

    if (state.pickupDropOff) {
      this.pickupDropOffState.set(hydratePickupDropOffFromSavePayload(state.pickupDropOff));
    }

    this.hasDeliverySelection.set(Boolean(state.selectedService));

    this.signatureProtectionForm.controls.requirement.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.refreshDeliveryEstimatesForAccessorials();
      });

    if (this.pickupDropOffState().method === 'drop-off') {
      void this.loadNearestDropOff();
    }

    void this.bootstrapPickupOnEnter();
  }

  /**
   * Re-orchestrates delivery options when signature or declared value changes.
   */
  private refreshDeliveryEstimatesForAccessorials(): void {
    const coverage = buildAdditionalCoverageSavePayload(this.additionalCoverageForm);
    this.deliverySelection()?.refreshWithShipmentServices({
      signatureRequirement: this.signatureProtectionForm.controls.requirement.value,
      declaredValue: coverage.declaredValue,
    });
  }

  /**
   * Re-orchestrates delivery options after Declared Value is committed on blur.
   */
  protected onDeclaredValueCommitted(): void {
    this.refreshDeliveryEstimatesForAccessorials();
  }

  /**
   * Persists the current induction method and pickup draft before leaving the Service step
   * so Back / navigate-away retains unsaved selection.
   */
  ngOnDestroy(): void {
    this.persistPickupDropOffProgress();
  }

  /**
   * Updates the active induction method, retaining previously entered pickup fields,
   * and loads pickup availability/locations when Schedule a pickup is selected.
   * Ignores pickup when config has disabled scheduling.
   */
  protected onMethodChange(method: InductionMethod): void {
    if (method === 'pickup' && !this.pickupSchedulingEnabled()) {
      return;
    }

    this.shipmentFlowState.clearAttestation('pickupValidation');
    this.pickupDropOffState.set(setInductionMethod(this.pickupDropOffState(), method));
    this.showValidationErrors.set(false);
    this.windowNoticeKind.set(null);
    if (method !== 'pickup') {
      this.availabilityNoticeKind.set(null);
      this.availabilityMessageSlot.set(null);
    }
    this.persistPickupDropOffProgress();
    this.analytics.trackEvent({
      event: 'Is pickup / dropoff',
      isPickup: method === 'pickup',
      isDropoff: method === 'drop-off',
    });

    if (method === 'pickup') {
      void this.loadPickupData();
    } else if (method === 'drop-off') {
      void this.loadNearestDropOff();
    }
  }

  /**
   * Applies a selected pickup date and seeds the default window from that slot.
   */
  protected onDateChange(date: string): void {
    this.shipmentFlowState.clearAttestation('pickupValidation');
    const availability = this.pickupDropOffState().availability ?? [];
    const clock = this.shipFromLocalClock();
    const defaults = selectPickupDateDefaults(availability, date, {
      todayIso: formatLocalIsoDate(new Date(), clock),
      currentLocalHhmm: formatCurrentLocalHhmm(new Date(), clock),
    });
    this.pickupDropOffState.set({
      ...this.pickupDropOffState(),
      selectedDate: defaults.selectedDate,
      windowFrom: defaults.windowFrom,
      windowTo: defaults.windowTo,
    });
    this.windowNoticeKind.set(
      defaults.windowFrom && defaults.windowTo ? null : 'noValidWindowForDate',
    );
  }

  /**
   * Applies ready/close window HHmm values, recalculating To when From changes so the
   * selected close time always meets the Salesforce minimum duration.
   */
  protected onWindowChange(change: PickupWindowChange): void {
    this.shipmentFlowState.clearAttestation('pickupValidation');
    const state = this.pickupDropOffState();
    const slot = (state.availability ?? []).find((entry) => entry.date === state.selectedDate);
    if (!slot || !change.from) {
      this.pickupDropOffState.set({
        ...state,
        windowFrom: change.from,
        windowTo: change.to,
      });
      return;
    }

    const clock = this.shipFromLocalClock();
    const todayIso = formatLocalIsoDate(new Date(), clock);
    const currentLocalHhmm = formatCurrentLocalHhmm(new Date(), clock);
    const { fromOptions, toOptions } = buildWindowOptionsForSlot(slot, change.from, {
      todayIso,
      currentLocalHhmm,
    });
    const reconciled = resolvePickupWindowSelection({
      from: change.from,
      to: change.to,
      fromOptions,
      toOptions,
    });

    this.pickupDropOffState.set({
      ...state,
      windowFrom: reconciled.from,
      windowTo: reconciled.to,
    });

    this.windowNoticeKind.set(reconciled.hasValidWindow ? null : 'windowTooSmall');
  }

  /**
   * Applies the selected pickup-from location code.
   */
  protected onLocationCodeChange(locationCode: string | null): void {
    this.shipmentFlowState.clearAttestation('pickupValidation');
    this.pickupDropOffState.set({
      ...this.pickupDropOffState(),
      locationCode,
    });
  }

  /**
   * Applies courier instructions from the pickup address-contact section (`maxLength` caps input).
   */
  protected onCourierInstructionsChange(courierInstructions: string): void {
    this.pickupDropOffState.set({
      ...this.pickupDropOffState(),
      courierInstructions,
    });
    this.analytics.trackEvent({
      event: 'Pickup additional instruction.',
      hasInstructions: courierInstructions.trim().length > 0,
    });
  }

  /**
   * Whether the active pickup/drop-off state satisfies Save & Continue rules for the method.
   */
  protected isPickupValid(): boolean {
    return isPickupDropOffValid(this.pickupDropOffState());
  }

  /**
   * Tracks whether the delivery matrix currently has a selected offering and refreshes
   * locator/pickup data when transportation mode may have changed.
   */
  protected onDeliverySelectionChange(selection: SelectedDeliveryService | null): void {
    this.hasDeliverySelection.set(selection !== null);
    this.applySignatureRequirementForProduct(selection?.productCode ?? null);
    if (selection) {
      this.showValidationErrors.set(false);
    }

    const routeKey = this.buildPickupRouteKey(selection);
    if (routeKey === this.lastPickupRouteKey) {
      return;
    }
    this.shipmentFlowState.clearAttestation('pickupValidation');
    this.lastPickupRouteKey = routeKey;

    const method = this.pickupDropOffState().method;
    if (this.pickupSchedulingEnabled()) {
      void this.ensurePickupValidateCache();
    }
    if (method === 'drop-off') {
      void this.loadNearestDropOff();
    } else if (method === 'pickup') {
      void this.loadPickupData();
    }
  }

  /**
   * Builds a stable key for pickup/locator routing from selection presence and route codes.
   */
  private buildPickupRouteKey(selection: SelectedDeliveryService | null): string {
    const routeCodes = this.resolvePickupRouteCodesFromFlow();
    const presence = selection ? 'selected' : 'none';
    return `${presence}|${routeCodes.transportationMode}|${routeCodes.destinationCode}`;
  }

  /**
   * Filters signature options from the selected product's Product Master relations
   * and applies OM-347 defaults on product change. Keeps the current value for same-product
   * re-rates and for the first apply after a Review restore (OM-725).
   */
  private applySignatureRequirementForProduct(productCode: string | null): void {
    if (!productCode) {
      return;
    }
    const relations = this.deliverySelection()?.getProductRelations(productCode) ?? [];
    const flow = this.shipmentFlowState.load();
    const destinationResidential = resolveDestinationResidential(flow ?? {});
    const productChanged = this.lastSignatureProductCode !== productCode;
    const keepCurrent = this.signatureSelectionCommitted || !productChanged;
    const resolved = resolveSignatureRequirement({
      relations,
      destinationResidential,
      current: keepCurrent ? this.signatureProtectionForm.controls.requirement.value : undefined,
    });

    this.signatureAvailableRequirements.set(resolved.options);
    this.signatureIncludedRequirements.set(inclusiveSignatureRequirements(relations));
    this.signatureDestinationResidential.set(destinationResidential);

    const requirementChanged =
      this.signatureProtectionForm.controls.requirement.value !== resolved.requirement;
    if (requirementChanged) {
      this.signatureProtectionForm.controls.requirement.setValue(resolved.requirement, {
        emitEvent: false,
      });
    }

    this.lastSignatureProductCode = productCode;
    this.signatureSelectionCommitted = false;

    if (requirementChanged) {
      this.refreshDeliveryEstimatesForAccessorials();
    }
  }

  /**
   * Stores the Estimated Costs breakdown emitted by delivery selection for the sticky sidebar.
   */
  protected onEstimatedBreakdownChange(breakdown: EstimateBreakdownView | null): void {
    this.estimatedBreakdown.set(breakdown);
  }

  /**
   * Syncs Estimated Costs skeleton visibility with the delivery rate-call region.
   */
  protected onDeliverySkeletonChange(show: boolean): void {
    this.showDeliverySkeleton.set(show);
  }

  /**
   * Tracks whether delivery offerings have settled so signature/coverage stay hidden
   * during the delayed-skeleton gap (avoids a flash of those sections).
   */
  protected onDeliveryOfferingsUnresolvedChange(unresolved: boolean): void {
    this.deliveryOfferingsUnresolved.set(unresolved);
  }

  /**
   * Navigates back to Package when Back is clicked (page-owned navigation avoids RouterLink
   * duplicate tab stops on puro-button — audit ID 13).
   */
  protected onBack(): void {
    if (this.saveAndContinueInProgress()) {
      return;
    }
    void this.router.navigateByUrl('/package');
  }

  /**
   * Validates delivery service, Additional Coverage, signature, and pickup/drop-off, then
   * persists when valid and advances to Review & Pay. For pickup, re-validates the selected
   * window through Salesforce before saving.
   */
  protected async onSaveAttempted(): Promise<void> {
    if (this.deliveryRegionBusy() || this.saveAndContinueInProgress()) {
      return;
    }

    this.showValidationErrors.set(true);
    this.additionalCoverageForm.markAllAsTouched();
    this.additionalCoverageForm.updateValueAndValidity();
    this.signatureProtectionForm.markAllAsTouched();
    this.signatureProtectionForm.updateValueAndValidity();

    const delivery = this.deliverySelection();
    if (delivery && !delivery.hasSelection()) {
      delivery.showRequiredSelectionError();
      return;
    }

    if (!this.hasDeliverySelection()) {
      delivery?.showRequiredSelectionError();
      return;
    }

    const pickupValid = isPickupDropOffValid(this.pickupDropOffState());
    const coverageValid = isAdditionalCoverageValid(this.additionalCoverageForm);
    const signatureValid = isSignatureProtectionValid(this.signatureProtectionForm);
    if (!pickupValid || !coverageValid || !signatureValid) {
      return;
    }

    this.saveAndContinueInProgress.set(true);
    try {
      if (this.pickupDropOffState().method === 'pickup') {
        const windowOk = await this.validateSelectedPickupWindow();
        if (!windowOk) {
          return;
        }
      }

      const pickupDropOff = buildPickupDropOffSavePayload(this.pickupDropOffState());
      const shipFrom = this.shipFrom();
      if (pickupDropOff.method === 'pickup' && shipFrom) {
        const routeCodes = this.resolvePickupRouteCodesFromFlow();
        const scheduleRequest = prepareSchedulePickupRequest({
          state: this.pickupDropOffState(),
          shipFrom,
          currentLocalTime: formatCurrentLocalTime(new Date(), this.shipFromLocalClock()),
          transportationMode: routeCodes.transportationMode,
          destinationCode: routeCodes.destinationCode,
        });
        if (scheduleRequest) {
          pickupDropOff.scheduleRequest = scheduleRequest;
        }
      }

      this.showValidationErrors.set(false);
      this.trackPickupDropOffSaveAnalytics();
      this.shipmentFlowState.saveServiceStep({
        pickupDropOff,
        additionalCoverage: buildAdditionalCoverageSavePayload(this.additionalCoverageForm),
        signatureProtection: buildSignatureProtectionSavePayload(this.signatureProtectionForm),
      });
      await this.router.navigate(['/review']);
    } finally {
      this.saveAndContinueInProgress.set(false);
    }
  }

  /**
   * Publishes sanitized PickupDetails or DropoffDetails when Save & Continue succeeds (OM-807).
   */
  private trackPickupDropOffSaveAnalytics(): void {
    const state = this.pickupDropOffState();
    if (state.method === 'pickup') {
      this.analytics.trackEvent({
        event: 'PickupDetails',
        hasDate: Boolean(state.selectedDate),
        hasWindow: Boolean(state.windowFrom && state.windowTo),
      });
      return;
    }
    if (state.method === 'drop-off') {
      this.analytics.trackEvent({
        event: 'DropoffDetails',
        hasNearestLocation: state.nearestDropOff != null,
      });
    }
  }

  /**
   * Sends pickup validation with the active flow id so returned JWTs are flow-bound.
   */
  private validatePickup(request: PickupValidateRequest): Promise<PickupValidateResponse> {
    return this.pickupApi.validate(request, this.shipmentFlowState.ensureFlowId());
  }

  /**
   * Builds a Pickup Validate request using the persisted Service Ability attestation.
   * Returns null when the token is missing so callers fail closed without inventing an address.
   * Adds the account-lookup attestation when present so the backend can decide weekend
   * eligibility; without it the backend never offers weekend dates.
   */
  private buildPickupValidateRequest(
    fields: Omit<
      PickupValidateRequest,
      'serviceAbilityToken' | 'accountLookupToken' | 'currentLocalTime'
    > & {
      currentLocalTime?: string;
    },
  ): PickupValidateRequest | null {
    const attestations = this.shipmentFlowState.load()?.attestations;
    const serviceAbilityToken = attestations?.serviceAbility?.trim();
    if (!serviceAbilityToken) {
      return null;
    }
    const accountLookupToken = attestations?.account?.trim();

    return {
      currentLocalTime:
        fields.currentLocalTime ?? formatCurrentLocalTime(new Date(), this.shipFromLocalClock()),
      serviceAbilityToken,
      ...(accountLookupToken ? { accountLookupToken } : {}),
      transportationMode: fields.transportationMode,
      destinationCode: fields.destinationCode,
      ...(fields.selectedPickupDate !== undefined
        ? { selectedPickupDate: fields.selectedPickupDate }
        : {}),
      ...(fields.pickUpAnyTimeAfter !== undefined
        ? { pickUpAnyTimeAfter: fields.pickUpAnyTimeAfter }
        : {}),
      ...(fields.pickUpUntilTime !== undefined ? { pickUpUntilTime: fields.pickUpUntilTime } : {}),
      ...(fields.pickupLocationCode !== undefined
        ? { pickupLocationCode: fields.pickupLocationCode }
        : {}),
      ...(fields.additionalInstructions !== undefined
        ? { additionalInstructions: fields.additionalInstructions }
        : {}),
    };
  }

  /**
   * Reads `pickupEnabled` from the pickup lambda as soon as Service loads, before
   * locations or Salesforce availability work. Disabled scheduling skips those calls.
   */
  private async bootstrapPickupOnEnter(): Promise<void> {
    const shipFrom = this.shipFrom();
    if (!shipFrom) {
      return;
    }

    const loadPickupAfterConfig = this.pickupDropOffState().method === 'pickup';
    if (loadPickupAfterConfig) {
      this.availabilityLoading.set(true);
      this.locationsLoading.set(true);
    }

    const routeCodes = this.resolvePickupRouteCodesFromFlow();
    const cacheKey = this.pickupValidateCacheKey(shipFrom, routeCodes);

    try {
      const request = this.buildPickupValidateRequest({
        transportationMode: routeCodes.transportationMode,
        destinationCode: routeCodes.destinationCode,
      });
      if (!request) {
        this.pickupValidateCache = null;
        this.pickupSchedulingEnabled.set(true);
        return;
      }
      const response = await this.validatePickup(request);
      this.pickupValidateCache = { key: cacheKey, response };
      this.applyPickupEnabledFromResponse(response);
    } catch {
      this.pickupValidateCache = null;
      this.pickupSchedulingEnabled.set(true);
    }

    if (!this.pickupSchedulingEnabled()) {
      this.applyPickupSchedulingDisabled();
      this.availabilityLoading.set(false);
      this.locationsLoading.set(false);
      return;
    }

    void this.prefetchPickupLocations();
    if (loadPickupAfterConfig) {
      await this.loadPickupData();
    }
  }

  /**
   * Refreshes the validate cache when ship-from or route codes no longer match the
   * enter-time response, so Schedule a pickup can reuse the latest body.
   */
  private async ensurePickupValidateCache(): Promise<void> {
    const shipFrom = this.shipFrom();
    if (!shipFrom || !this.pickupSchedulingEnabled()) {
      return;
    }

    const routeCodes = this.resolvePickupRouteCodesFromFlow();
    const cacheKey = this.pickupValidateCacheKey(shipFrom, routeCodes);
    if (this.pickupValidateCache?.key === cacheKey) {
      return;
    }

    try {
      const request = this.buildPickupValidateRequest({
        transportationMode: routeCodes.transportationMode,
        destinationCode: routeCodes.destinationCode,
      });
      if (!request) {
        this.pickupValidateCache = null;
        return;
      }
      const response = await this.validatePickup(request);
      this.pickupValidateCache = { key: cacheKey, response };
      this.applyPickupEnabledFromResponse(response);
    } catch {
      this.pickupValidateCache = null;
    }

    if (!this.pickupSchedulingEnabled()) {
      this.applyPickupSchedulingDisabled();
    }
  }

  /**
   * Forces Drop-off when config disables pickup, then loads nearest drop-off so the
   * customer can continue without a blank locator panel.
   */
  private applyPickupSchedulingDisabled(): void {
    if (this.pickupDropOffState().method === 'pickup') {
      this.pickupDropOffState.set(setInductionMethod(this.pickupDropOffState(), 'drop-off'));
    }
    this.persistPickupDropOffProgress();
    void this.loadNearestDropOff();
  }

  /**
   * Cache key for reuse of enter-time validate until address or route codes change.
   */
  private pickupValidateCacheKey(shipFrom: AddressFormValue, routeCodes: PickupRouteCodes): string {
    return [
      shipFrom.postalCode?.trim() ?? '',
      routeCodes.transportationMode,
      routeCodes.destinationCode,
    ].join('|');
  }

  /**
   * Applies the validate `pickupEnabled` flag. Missing is treated as enabled.
   */
  private applyPickupEnabledFromResponse(response: PickupValidateResponse): void {
    this.pickupSchedulingEnabled.set(response.pickupEnabled !== false);
  }

  /**
   * Loads nearest drop-off via `POST /locator/search` using the validated Ship From postal.
   * Transportation and destination follow the selected delivery service and Ship To country.
   * Failures show a customer message and never block Save & Continue.
   */
  private async loadNearestDropOff(): Promise<void> {
    const generation = ++this.locatorLoadGeneration;
    const shipFrom = this.shipFrom();
    const postalCode = shipFrom?.postalCode?.trim() ?? '';

    if (!postalCode) {
      this.locatorErrorActive.set(false);
      this.locatorLoading.set(false);
      this.pickupDropOffState.set({
        ...this.pickupDropOffState(),
        nearestDropOff: null,
      });
      return;
    }

    this.locatorLoading.set(true);
    this.locatorErrorActive.set(false);

    try {
      const routeCodes = this.resolvePickupRouteCodesFromFlow();
      const response = await this.locatorApi.search({
        postalCode,
        destinationCode: routeCodes.destinationCode,
        transportationMode: routeCodes.transportationMode,
      });

      if (generation !== this.locatorLoadGeneration) {
        return;
      }

      const nearest = response.locations[0] ?? null;
      this.pickupDropOffState.set({
        ...this.pickupDropOffState(),
        nearestDropOff: nearest,
      });
      this.locatorErrorActive.set(!nearest);
    } catch {
      if (generation !== this.locatorLoadGeneration) {
        return;
      }
      this.pickupDropOffState.set({
        ...this.pickupDropOffState(),
        nearestDropOff: null,
      });
      this.locatorErrorActive.set(true);
    } finally {
      if (generation === this.locatorLoadGeneration) {
        this.locatorLoading.set(false);
      }
    }
  }

  /**
   * Loads `POST /pickup/validate` and applies cached or fetched `GET /pickup/locations`.
   * Transportation and destination follow the selected delivery service and Ship To country.
   */
  private async loadPickupData(): Promise<void> {
    const generation = ++this.pickupLoadGeneration;
    const shipFrom = this.shipFrom();

    if (!shipFrom) {
      this.windowNoticeKind.set(null);
      this.availabilityNoticeKind.set('serviceUnavailable');
      this.availabilityMessageSlot.set('error');
      return;
    }

    const language = this.translation.locale() === 'fr' ? 'FR' : 'EN';
    const locale = this.translation.locale() === 'fr' ? 'fr' : 'en';
    const needsLocationsFetch = !this.hasWarmLocationsCache(language);
    const routeCodes = this.resolvePickupRouteCodesFromFlow();
    const cacheKey = this.pickupValidateCacheKey(shipFrom, routeCodes);
    const cached =
      this.pickupValidateCache?.key === cacheKey ? this.pickupValidateCache.response : null;

    this.availabilityLoading.set(true);
    this.locationsLoading.set(needsLocationsFetch);
    this.availabilityNoticeKind.set(null);
    this.availabilityMessageSlot.set(null);
    this.windowNoticeKind.set(null);
    if (needsLocationsFetch) {
      this.locationsErrorActive.set(false);
    }

    const validatePromise = cached
      ? Promise.resolve(cached)
      : (() => {
          const request = this.buildPickupValidateRequest({
            transportationMode: routeCodes.transportationMode,
            destinationCode: routeCodes.destinationCode,
          });
          if (!request) {
            return Promise.reject(new Error('serviceAbilityToken required'));
          }
          return this.validatePickup(request);
        })();
    const locationsPromise = this.resolvePickupLocations(language);

    const [validateResult, locationsResult] = await Promise.allSettled([
      validatePromise,
      locationsPromise,
    ]);

    if (generation !== this.pickupLoadGeneration) {
      return;
    }

    this.availabilityLoading.set(false);
    this.locationsLoading.set(false);

    if (validateResult.status === 'fulfilled') {
      this.pickupValidateCache = { key: cacheKey, response: validateResult.value };
      this.applyPickupEnabledFromResponse(validateResult.value);
      this.applyValidateResponse(
        validateResult.value.pickUps,
        validateResult.value.errorMessage,
        locale,
      );
    } else {
      this.windowNoticeKind.set(null);
      this.availabilityNoticeKind.set('serviceUnavailable');
      this.availabilityMessageSlot.set('error');
      this.pickupDropOffState.set({
        ...this.pickupDropOffState(),
        availability: [],
        selectedDate: null,
        windowFrom: null,
        windowTo: null,
      });
    }

    if (locationsResult.status === 'fulfilled' && locationsResult.value) {
      this.applyPickupLocationsMap(locationsResult.value);
    } else if (locationsResult.status === 'rejected' || !locationsResult.value) {
      this.locationsErrorActive.set(true);
    }
  }

  /**
   * Prefetches pickup-from locations for the active locale when entering the Service step
   * or when the UI language changes.
   */
  private async prefetchPickupLocations(): Promise<void> {
    const language = this.translation.locale() === 'fr' ? 'FR' : 'EN';
    try {
      const map = await this.resolvePickupLocations(language);
      const currentLanguage: PickupLocationsLanguage =
        this.translation.locale() === 'fr' ? 'FR' : 'EN';
      // Ignore stale responses if the customer switched language while the request was in flight.
      if (map && currentLanguage === language) {
        this.applyPickupLocationsMap(map);
      }
    } catch {
      // Errors surface when the customer opens Schedule a pickup (loadPickupData).
    }
  }

  /**
   * Returns cached pickup-from map for the language, or fetches and caches a fresh response.
   * Concurrent callers for the same Accept-Language share one in-flight request so prefetch
   * and Pickup load do not double-hit; EN and FR never share a promise.
   * @returns The code→label map, or null when the request fails.
   */
  private async resolvePickupLocations(
    language: PickupLocationsLanguage,
  ): Promise<Record<string, string> | null> {
    if (this.hasWarmLocationsCache(language)) {
      return this.pickupLocationsCache!.pickupFrom;
    }

    const existingInFlight = this.pickupLocationsInFlight[language];
    if (existingInFlight) {
      return existingInFlight;
    }

    const inFlight = (async () => {
      try {
        const response = await this.pickupApi.getLocations(language);
        this.pickupLocationsCache = {
          language,
          pickupFrom: response.pickupFrom,
        };
        return response.pickupFrom;
      } catch {
        return null;
      } finally {
        delete this.pickupLocationsInFlight[language];
      }
    })();

    this.pickupLocationsInFlight[language] = inFlight;
    return inFlight;
  }

  /**
   * True when locations are already cached for the requested Accept-Language.
   */
  private hasWarmLocationsCache(language: PickupLocationsLanguage): boolean {
    return this.pickupLocationsCache?.language === language;
  }

  /**
   * Merges the locations map into state and applies Front Door default when needed.
   */
  private applyPickupLocationsMap(pickupFromMap: Record<string, string>): void {
    const previousCode = this.pickupDropOffState().locationCode;
    this.pickupDropOffState.set({
      ...this.pickupDropOffState(),
      pickupFromMap,
      locationCode: resolveDefaultPickupLocationCode(pickupFromMap, previousCode),
    });
    this.locationsErrorActive.set(false);
  }

  /**
   * Merges validate `pickUps` into state, maps story availability notices, and prefers a
   * previously selected date/window when they remain eligible.
   */
  private applyValidateResponse(
    pickUps: PickupAvailabilitySlot[],
    errorMessage: string | null,
    locale: 'en' | 'fr',
  ): void {
    const clock = this.shipFromLocalClock();
    const today = formatLocalIsoDate(new Date(), clock);
    const currentLocalHhmm = formatCurrentLocalHhmm(new Date(), clock);
    const notice = mapPickupAvailabilityNotice({
      pickUps,
      errorMessage,
      todayIso: today,
      validateRejected: false,
    });

    if (!notice.showDates) {
      this.windowNoticeKind.set(null);
      this.availabilityNoticeKind.set(notice.kind);
      // Rich alert with Drop-off link is rendered by the shell for this kind.
      this.availabilityMessageSlot.set(
        notice.kind && notice.kind !== 'notSupportedAtAddress' ? 'error' : null,
      );
      this.pickupDropOffState.set({
        ...this.pickupDropOffState(),
        availability: [],
        selectedDate: null,
        windowFrom: null,
        windowTo: null,
      });
      return;
    }

    this.availabilityNoticeKind.set(notice.kind);
    this.availabilityMessageSlot.set(notice.kind ? 'notice' : null);

    const previous = this.pickupDropOffState();
    const preferredDate =
      previous.selectedDate && pickUps.some((slot) => slot.date === previous.selectedDate)
        ? previous.selectedDate
        : mapValidatePickUpsToDateSelection(pickUps, {
            today,
            locale,
            currentLocalHhmm,
            todayLabelTemplate: this.translation.translate(
              'createShipment.serviceStep.pickupDropOff.dateTodayLabel',
            ),
          }).selectedDate;

    const defaults = preferredDate
      ? selectPickupDateDefaults(pickUps, preferredDate, {
          todayIso: today,
          currentLocalHhmm,
        })
      : { selectedDate: null, windowFrom: null, windowTo: null };

    const retained = this.retainWindowIfStillValid(
      pickUps,
      preferredDate,
      previous.windowFrom,
      previous.windowTo,
      today,
      currentLocalHhmm,
    );

    this.pickupDropOffState.set({
      ...previous,
      availability: pickUps,
      selectedDate: retained.selectedDate ?? defaults.selectedDate,
      windowFrom: retained.windowFrom ?? defaults.windowFrom,
      windowTo: retained.windowTo ?? defaults.windowTo,
    });

    const hasWindow = Boolean(
      (retained.windowFrom ?? defaults.windowFrom) && (retained.windowTo ?? defaults.windowTo),
    );
    this.windowNoticeKind.set(hasWindow ? null : 'noValidWindowForDate');
  }

  /**
   * Keeps a previously chosen From/To when the customer navigates back, as long as both
   * values remain selectable for the preferred date.
   */
  private retainWindowIfStillValid(
    pickUps: PickupAvailabilitySlot[],
    preferredDate: string | null,
    previousFrom: string | null,
    previousTo: string | null,
    todayIso: string,
    currentLocalHhmm: string,
  ): { selectedDate: string | null; windowFrom: string | null; windowTo: string | null } {
    if (!preferredDate || !previousFrom || !previousTo) {
      return { selectedDate: null, windowFrom: null, windowTo: null };
    }

    const slot = pickUps.find((entry) => entry.date === preferredDate);
    if (!slot) {
      return { selectedDate: null, windowFrom: null, windowTo: null };
    }

    const { fromOptions, toOptions } = buildWindowOptionsForSlot(slot, previousFrom, {
      todayIso,
      currentLocalHhmm,
    });
    const selection = resolvePickupWindowSelection({
      from: previousFrom,
      to: previousTo,
      fromOptions,
      toOptions,
    });

    if (
      !selection.hasValidWindow ||
      selection.from !== previousFrom ||
      selection.to !== previousTo
    ) {
      return { selectedDate: null, windowFrom: null, windowTo: null };
    }

    return {
      selectedDate: preferredDate,
      windowFrom: previousFrom,
      windowTo: previousTo,
    };
  }

  /**
   * Re-validates the customer-selected pickup window through Salesforce via
   * `POST /pickup/validate` with ready/close times (and optional courier
   * `additionalInstructions` for JWT_pickup → Salesforce `courierRemarks`, OM-830).
   * Maps failures to story messages without exposing raw Salesforce codes. When slots
   * remain eligible but JWT_pickup is missing (OM-703), surfaces service-unavailable
   * instead of a time-window error.
   */
  private async validateSelectedPickupWindow(): Promise<boolean> {
    this.shipmentFlowState.clearAttestation('pickupValidation');
    const shipFrom = this.shipFrom();
    const state = this.pickupDropOffState();
    if (!shipFrom || !state.selectedDate || !state.windowFrom || !state.windowTo) {
      return false;
    }

    try {
      const routeCodes = this.resolvePickupRouteCodesFromFlow();
      const courierInstructions = state.courierInstructions.trim();
      const request = this.buildPickupValidateRequest({
        transportationMode: routeCodes.transportationMode,
        destinationCode: routeCodes.destinationCode,
        selectedPickupDate: state.selectedDate,
        pickUpAnyTimeAfter: state.windowFrom,
        pickUpUntilTime: state.windowTo,
        pickupLocationCode: state.locationCode ?? undefined,
        ...(courierInstructions ? { additionalInstructions: courierInstructions } : {}),
      });
      if (!request) {
        this.windowNoticeKind.set('unableToScheduleSelectedTime');
        return false;
      }
      const response = await this.validatePickup(request);

      const confirmedSlot = response.pickUps.find((slot) => slot.date === state.selectedDate);
      if (!confirmedSlot || response.errorMessage) {
        this.windowNoticeKind.set('unableToScheduleSelectedTime');
        return false;
      }

      // OM-740: Salesforce's own /pickups/validate does not reject a submitted window
      // shorter than its cutoff requirement (it only reports `cutOff` as metadata), so a
      // window selected under a previous transportation mode can slip through here and
      // still fail the real /pickups/schedule call later with errorCode 700. Re-check the
      // selected window against the confirmed slot's minimum duration before proceeding.
      const selectedWindowMinutes = hhmmToMinutes(state.windowTo) - hhmmToMinutes(state.windowFrom);
      if (selectedWindowMinutes < resolveMinimumPickupWindowMinutes(confirmedSlot)) {
        this.windowNoticeKind.set('windowTooSmall');
        return false;
      }

      const pickupAttestation = response.attestation?.trim();
      if (!pickupAttestation) {
        // OM-703: slots can succeed while JWT_pickup is missing (STG deploy/secret gap).
        // Do not blame the selected time — surface the same service-unavailable path as throws.
        this.availabilityNoticeKind.set('serviceUnavailable');
        this.availabilityMessageSlot.set('error');
        this.windowNoticeKind.set(null);
        return false;
      }

      this.shipmentFlowState.saveAttestation('pickupValidation', pickupAttestation);
      this.windowNoticeKind.set(null);
      return true;
    } catch {
      this.availabilityNoticeKind.set('serviceUnavailable');
      this.availabilityMessageSlot.set('error');
      this.windowNoticeKind.set(null);
      return false;
    }
  }

  /**
   * Resolves a customer-facing availability message, interpolating the product phone
   * placeholder for service-unavailable copy.
   */
  private translateAvailabilityNotice(kind: PickupAvailabilityNoticeKind): string {
    const key = pickupAvailabilityNoticeI18nKey(kind);
    if (kind === 'serviceUnavailable') {
      const phone = this.translation.translate(
        'createShipment.serviceStep.pickupDropOff.availability.supportPhone',
      );
      return this.translation.translate(key, { phone });
    }
    return this.translation.translate(key);
  }

  /**
   * Resolves a customer-facing pickup-window message under `windowErrors`.
   */
  private translateWindowNotice(kind: PickupWindowNoticeKind): string {
    return this.translation.translate(
      `createShipment.serviceStep.pickupDropOff.windowErrors.${kind}`,
    );
  }

  /**
   * Writes the current induction method and pickup draft to flow state without completing
   * the Service step (Back / method toggle retention).
   */
  private persistPickupDropOffProgress(): void {
    this.shipmentFlowState.persistPickupDropOffProgress(
      buildPickupDropOffSavePayload(this.pickupDropOffState()),
    );
  }

  /**
   * Builds Ship From clock options so same-day From defaults and validate `currentLocalTime`
   * use the Ship From province timezone, not the browser timezone.
   */
  private shipFromLocalClock(): ShipFromLocalClockOptions {
    return { provinceCode: this.shipFrom()?.province ?? null };
  }

  /**
   * Resolves pickup/locator transportation and destination codes from the selected
   * delivery service. OLS addresses are Canada-first (no country field yet), so
   * destination defaults to DOM via `resolvePickupRouteCodes`.
   */
  private resolvePickupRouteCodesFromFlow(): PickupRouteCodes {
    const selected = this.shipmentFlowState.load()?.selectedService;
    return resolvePickupRouteCodes({
      transportationMode: selected?.transportationMode,
      shipToCountry: null,
    });
  }
}
